import { PRIMARY } from '../Theme/palette';
import { ServiceSelect } from '@components';
import { useTenantVat } from '@hooks';
import { L } from '@lib';
import { InitialServiceOrderExtra, ServiceOrder, ServiceOrderExtra } from '@services';
import { useStore } from '@stores';
import { Rules } from '@utils';
import { Col, ConfigProvider, Form, FormInstance, Input, Row, Segmented, Select } from 'antd';
import { observer } from 'mobx-react';
import React from 'react';
import { SharedForm } from './SharedForm';

const { Option } = Select;

interface IProps {
  form: FormInstance<any>;
  serviceOrders: ServiceOrder[];
  onFinish: (serviceOrderExtra: ServiceOrderExtra) => Promise<void>;
  onCancel: (value: boolean) => void;
}

export const ServiceOrderExtraForm: React.FC<IProps> = observer(
  ({ form, serviceOrders, onFinish, onCancel }) => {
    const { serviceStore } = useStore();
    const { selectServices } = serviceStore;
    const type = Form.useWatch('type', form);

    const vat = useTenantVat();

    const onServiceSelect = (id: number) => {
      const service = selectServices.get(id);

      if (!service) return;
      form.setFieldsValue({
        title: `${service.name}${service.isFixed ? '' : ' - Labour '}`,
        price: service.isFixed ? service.price : undefined,
        duration: service.duration || undefined,
        vat: service.isVat ? vat : 0,
        discount: service.isFixed ? service.discount : undefined,
        isFixed: service.isFixed,
        serviceId: service.id,
      });
    };
    const handleFinish = async (serviceOrderExtra: ServiceOrderExtra) => {
      onFinish(serviceOrderExtra).then(() => {
        form.resetFields();
      });
    };

    const handleCancel = (value: boolean) => {
      onCancel(value);
      form.resetFields();
    };

    return (
      <SharedForm
        form={form}
        onFinish={handleFinish}
        onCancel={handleCancel}
        style={{ marginTop: 24 }}
        name="createExtra"
        initialValues={{ ...InitialServiceOrderExtra, type: 'Part' }}
        submitTitle="Save"
      >
        <Row>
          <Col span={24}>
            <ConfigProvider
              theme={{
                components: {
                  Segmented: {
                    itemSelectedBg: PRIMARY,
                    itemSelectedColor: '#fff',
                    trackBg: '#f0f0f0',
                  },
                },
              }}
            >
              {/* Segmented must be the direct child so the Form injects value/onChange into it. */}
              <Form.Item name="type" label={L('Type')} rules={Rules.required}>
                <Segmented
                  block
                  size="large"
                  options={[
                    { label: L('Part'), value: 'Part' },
                    { label: L('Fluid'), value: 'Fluid' },
                    { label: L('Service'), value: 'Service' },
                  ]}
                />
              </Form.Item>
            </ConfigProvider>
          </Col>
        </Row>
        {type !== 'Service' ? (
              <>
                {serviceOrders.length === 1 && (
                  <Form.Item name="serviceOrderId" hidden>
                    <Input />
                  </Form.Item>
                )}
                <Row gutter={10}>
                  {serviceOrders.length > 1 && (
                    <Col span={12}>
                      <Form.Item
                        name="serviceOrderId"
                        label={L('Select service')}
                        rules={Rules.required}
                      >
                        <Select placeholder={L('Select service')}>
                          {serviceOrders.map((serviceOrder, index) => (
                            <Option key={serviceOrder.title + index} value={serviceOrder.id}>
                              {serviceOrder.title}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  )}
                  <Col span={serviceOrders.length > 1 ? 12 : 24}>
                    <Form.Item name="name" label={L('Name')} rules={Rules.required}>
                      <Input placeholder={L('Enter name')} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={10}>
                  <Col span={12}>
                    <Form.Item name="quantity" label={L('Unit / Litre')} rules={Rules.required}>
                      <Input
                        type="number"
                        min={0}
                        step=".01"
                        placeholder={L('Enter unit / litre')}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="price"
                      label={L('Price per unit / litre (£)')}
                      rules={Rules.required}
                    >
                      <Input
                        type="number"
                        min={0}
                        step=".01"
                        placeholder={L('Enter price per unit / litre (£)')}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={10}>
                  <Col span={12}>
                    <Form.Item name="discount" label={L('Discount %')}>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step=".01"
                        placeholder={L('Enter discount')}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="number" label={L('Reference number')}>
                      <Input placeholder={L('Enter ref number')} />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            ) : (
              <>
                <Row>
                  <Col span={24}>
                    <Form.Item name="serviceId" label={L('Service')} rules={Rules.required}>
                      <ServiceSelect
                        showMyServicesOnly={true}
                        onSelect={onServiceSelect}
                        onClear={() => form.setFieldsValue({ serviceId: undefined })}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={10}>
                  <Col span={8}>
                    <Form.Item name="price" label={L('Price/hour (£)')} rules={Rules.required}>
                      <Input
                        type="number"
                        min={0}
                        step=".01"
                        placeholder={L('Enter price/hour (£)')}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="duration" label={L('Hours')} rules={Rules.required}>
                      <Input type="number" min={0} step=".01" placeholder={L('Enter hours')} />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item name="discount" label={L('Discounts %')}>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step=".01"
                        placeholder={L('Enter discount')}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item hidden name="title">
                  <Input />
                </Form.Item>
                <Form.Item hidden name="vat">
                  <Input />
                </Form.Item>
                <Form.Item hidden name="isFixed">
                  <Input />
                </Form.Item>
              </>
            )}
      </SharedForm>
    );
  }
);
